import React from 'react';
import styled from 'styled-components';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { C } from '../chrome';

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.6rem;
  border-top: 1px solid ${C.border};
  background: ${C.surface};
  font-size: 1.1rem;
  color: ${C.textMuted};
`;

const Range = styled.div`
  font-variant-numeric: tabular-nums;
  strong { color: ${C.textSecondary}; font-weight: 600; }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PageBtn = styled.button`
  background: transparent;
  border: 1px solid ${C.border};
  color: ${C.textMuted};
  border-radius: 0.5rem;
  padding: 0.5rem 0.9rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1.1rem;
  font-weight: 600;
  transition: all 0.15s;
  &:hover:not(:disabled) {
    border-color: ${C.purple};
    color: ${C.textPrimary};
    background: ${C.purpleDim};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Indicator = styled.span`
  padding: 0 0.6rem;
  font-variant-numeric: tabular-nums;
`;

/**
 * Compact pager — Prev / "Page X of Y" / Next on the right, "X–Y of N" on
 * the left. Caller owns the page state and just receives onChange(newPage).
 *
 * Props:
 *   page:     1-indexed current page (caller clamps if needed)
 *   pageSize: rows per page
 *   total:    total filtered rows (NOT the slice)
 *   onChange: (newPage) => void
 */
const Pagination = ({ page, pageSize, total, onChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage   = Math.min(Math.max(1, page), totalPages);
  const start      = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end        = Math.min(safePage * pageSize, total);

  return (
    <Bar>
      <Range>
        <strong>{start}</strong>–<strong>{end}</strong> of <strong>{total}</strong>
      </Range>
      <Controls>
        <PageBtn
          onClick={() => onChange(safePage - 1)}
          disabled={safePage <= 1}
        >
          <FiChevronLeft size={14} /> Prev
        </PageBtn>
        <Indicator>Page {safePage} of {totalPages}</Indicator>
        <PageBtn
          onClick={() => onChange(safePage + 1)}
          disabled={safePage >= totalPages}
        >
          Next <FiChevronRight size={14} />
        </PageBtn>
      </Controls>
    </Bar>
  );
};

/**
 * Pure helper: slice the filtered rows for a given page. Exposed so callers
 * can also derive their own "showing N of M filtered" copy.
 */
export const sliceForPage = (rows, page, pageSize) => {
  const start = (Math.max(1, page) - 1) * pageSize;
  return (rows || []).slice(start, start + pageSize);
};

export default Pagination;
